import {
  Injectable,
  ApplicationRef,
  createComponent,
  EnvironmentInjector,
  Type,
  ComponentRef,
  signal,
  computed,
  inject,
  DestroyRef,
  Injector,
  EmbeddedViewRef
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, fromEvent } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { DynamicSidebarComponent, SIDEBAR_TIMINGS } from './dynamic-sidebar.component';
import { SIDEBAR_CONFIG } from './sidebar-config';
import { SidebarViewMode } from './sidebar-view-mode';
import { SIDEBAR_CONTEXT } from './sidebar-context';
import { ExpandService } from '../expand.service';

export interface SidebarRouteConfig {
  startExpanded?: boolean;
  autoExpandOnSidebar?: boolean;
}

export interface SidebarEntityState {
  entityType: string | null;
  entityId: string | null;
  component: Type<any> | null;
  data: any;
}

@Injectable({
  providedIn: 'root'
})
export class SidebarService {
  private readonly appRef = inject(ApplicationRef);
  private readonly injector = inject(EnvironmentInjector);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly config = inject(SIDEBAR_CONFIG);
  private readonly parentInjector = inject(Injector);
  private readonly expandService = inject(ExpandService);

  private overlayComponentRef: ComponentRef<DynamicSidebarComponent> | null = null;
  private previousExpandState: boolean | null = null;
  private currentRouteConfig: SidebarRouteConfig = {};
  private persistentContentRef: ComponentRef<any> | null = null;
  private persistentContentElement: HTMLElement | null = null;
  private splitViewHostElement: HTMLElement | null = null;

  readonly isWideViewport = signal<boolean>(
    typeof window !== 'undefined' ? window.innerWidth >= this.config.breakpoint : false
  );

  readonly viewMode = computed<SidebarViewMode>(() =>
    this.isWideViewport() ? 'split' : 'overlay'
  );

  readonly selectedEntityId = signal<string | null>(null);
  readonly selectedEntityType = signal<string | null>(null);
  readonly selectedComponent = signal<Type<any> | null>(null);
  readonly selectedData = signal<any>(null);
  readonly contentReady = signal<boolean>(false);
  readonly isOpen = computed(() => this.selectedEntityId() !== null);
  readonly showSplitView = computed(() =>
    this.viewMode() === 'split' && this.isOpen()
  );
  readonly isSwitchingMode = signal<boolean>(false);

  constructor() {
    this.initViewportListener();
    this.initKeyboardListener();
    this.initRouteListener();
  }

  private initRouteListener(): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      let route = this.router.routerState.root;
      while (route.firstChild) {
        route = route.firstChild;
      }
      const sidebarConfig = route.snapshot.data?.['sidebar'] as SidebarRouteConfig | undefined;
      this.currentRouteConfig = sidebarConfig || {};
      if (this.currentRouteConfig.startExpanded && !this.isOpen()) {
        this.expandService.setExpandState(true);
      }
    });
  }

  private initViewportListener(): void {
    if (typeof window === 'undefined') return;
    fromEvent(window, 'resize').pipe(
      debounceTime(100),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      const wasWide = this.isWideViewport();
      const isNowWide = window.innerWidth >= this.config.breakpoint;
      if (wasWide !== isNowWide) {
        this.isWideViewport.set(isNowWide);
        if (this.isOpen()) {
          this.handleModeSwitch(isNowWide);
        }
      }
    });
  }

  private initKeyboardListener(): void {
    if (typeof window === 'undefined') return;
    fromEvent<KeyboardEvent>(window, 'keydown').pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((event) => {
      if (event.key === 'Escape' && this.isOpen()) {
        if (this.shouldSuppressEscape()) {
          return;
        }
        event.preventDefault();
        this.close();
      }
    });
  }

  private shouldSuppressEscape(): boolean {
    const activeElement = document.activeElement as HTMLElement;
    if (activeElement?.tagName === 'INPUT' ||
        activeElement?.tagName === 'TEXTAREA' ||
        activeElement?.tagName === 'SELECT') {
      activeElement.blur();
      return true;
    }
    if (activeElement?.getAttribute('contenteditable') === 'true') {
      activeElement.blur();
      return true;
    }
    if (document.querySelector('.p-dropdown-panel') ||
        document.querySelector('.p-autocomplete-panel') ||
        document.querySelector('.p-calendar-panel') ||
        document.querySelector('.p-multiselect-panel') ||
        document.querySelector('.p-overlaypanel') ||
        document.querySelector('.p-listbox-list-wrapper')) {
      return true;
    }
    if (document.querySelector('.p-dialog')) {
      return true;
    }
    return false;
  }

  open<T>(component: Type<T>, data?: any): void {
    const { entityType, entityId } = this.extractEntityInfo(data);
    const isSameComponent = this.selectedComponent() === component;
    const isSameEntity = this.selectedEntityId() === entityId;

    this.selectedComponent.set(component);
    this.selectedData.set(data);
    this.selectedEntityId.set(entityId);
    this.selectedEntityType.set(entityType);

    if (entityType && entityId) {
      this.updateUrl(entityType, entityId);
    }

    const autoExpand = this.currentRouteConfig.autoExpandOnSidebar !== false;
    if (this.viewMode() === 'split' && autoExpand && !this.expandService.isExpanded()) {
      this.previousExpandState = false;
      this.expandService.setExpandState(true);
    }

    if (!isSameComponent || !this.persistentContentRef || !isSameEntity) {
      this.createPersistentContent(component, data);
    }

    if (this.viewMode() === 'overlay') {
      this.openOverlay();
    } else {
      this.attachToSplitView();
    }
  }

  close(): void {
    this.selectedEntityId.set(null);
    this.selectedEntityType.set(null);
    this.selectedComponent.set(null);
    this.selectedData.set(null);
    this.contentReady.set(false);

    if (this.previousExpandState !== null) {
      this.expandService.setExpandState(this.previousExpandState);
      this.previousExpandState = null;
    }

    this.destroyPersistentContent();

    this.router.navigate([], {
      queryParams: {},
      queryParamsHandling: ''
    });

    this.closeOverlay();
  }

  registerSplitViewHost(hostElement: HTMLElement): void {
    this.splitViewHostElement = hostElement;
    if (this.persistentContentElement && this.isOpen()) {
      this.attachToSplitView();
    }
  }

  unregisterSplitViewHost(): void {
    this.splitViewHostElement = null;
  }

  getPersistentContentElement(): HTMLElement | null {
    return this.persistentContentElement;
  }

  private handleModeSwitch(toSplit: boolean): void {
    if (!this.persistentContentElement) return;

    this.isSwitchingMode.set(true);
    this.contentReady.set(false);

    if (toSplit) {
      if (this.overlayComponentRef) {
        this.overlayComponentRef.instance.detachContent();
        this.overlayComponentRef.instance.closeWithoutCallback();
        setTimeout(() => {
          if (this.overlayComponentRef) {
            this.appRef.detachView(this.overlayComponentRef.hostView);
            this.overlayComponentRef.destroy();
            this.overlayComponentRef = null;
          }
        }, SIDEBAR_TIMINGS.OVERLAY_ANIMATION_MS);
      }
      this.attachToSplitViewWithRetry();
    } else {
      setTimeout(() => {
        this.openOverlay();
        setTimeout(() => {
          this.isSwitchingMode.set(false);
          this.contentReady.set(true);
        }, SIDEBAR_TIMINGS.FADE_IN_DELAY_MS);
      }, SIDEBAR_TIMINGS.MODE_SWITCH_DELAY_MS);
    }
  }

  private attachToSplitViewWithRetry(attempt = 0): void {
    const maxAttempts = 10;
    if (this.attachToSplitView()) {
      this.isSwitchingMode.set(false);
    } else if (attempt < maxAttempts) {
      setTimeout(() => {
        this.attachToSplitViewWithRetry(attempt + 1);
      }, SIDEBAR_TIMINGS.RETRY_INTERVAL_MS);
    } else {
      console.warn('SidebarService: Split-view host not available after max retries, falling back to overlay');
      this.openOverlay();
      this.isSwitchingMode.set(false);
    }
  }

  private extractEntityInfo(data?: any): { entityType: string | null; entityId: string | null } {
    if (!data) return { entityType: null, entityId: null };
    const idKey = Object.keys(data).find(key =>
      key.endsWith('Id') && typeof data[key] === 'string'
    );
    if (idKey) {
      const entityType = idKey.replace(/Id$/, '').toLowerCase();
      return { entityType, entityId: data[idKey] };
    }
    const entityKey = Object.keys(data).find(key => data[key]?.id);
    if (entityKey) {
      return { entityType: entityKey.toLowerCase(), entityId: data[entityKey].id };
    }
    return { entityType: null, entityId: null };
  }

  private updateUrl(entityType: string, entityId: string): void {
    this.router.navigate([], {
      queryParams: { type: entityType, show: entityId },
      queryParamsHandling: 'merge'
    });
  }

  private createPersistentContent<T>(component: Type<T>, data?: any): void {
    this.destroyPersistentContent();

    const sidebarInjector = Injector.create({
      providers: [{ provide: SIDEBAR_CONTEXT, useValue: true }],
      parent: this.parentInjector
    });

    this.persistentContentRef = createComponent(component, {
      environmentInjector: this.injector,
      elementInjector: sidebarInjector
    });

    if (data) {
      Object.keys(data).forEach(key => {
        this.persistentContentRef!.instance[key] = data[key];
      });
    }

    this.appRef.attachView(this.persistentContentRef.hostView);

    const viewRef = this.persistentContentRef.hostView as EmbeddedViewRef<any>;
    this.persistentContentElement = viewRef.rootNodes[0] as HTMLElement;

    if (this.persistentContentElement) {
      this.persistentContentElement.style.height = '100%';
    }

    this.contentReady.set(true);
  }

  private destroyPersistentContent(): void {
    if (this.persistentContentRef) {
      this.appRef.detachView(this.persistentContentRef.hostView);
      this.persistentContentRef.destroy();
      this.persistentContentRef = null;
      this.persistentContentElement = null;
    }
  }

  private attachToSplitView(): boolean {
    if (!this.persistentContentElement) return false;
    if (!this.splitViewHostElement) return false;

    if (this.persistentContentElement.parentNode &&
        this.persistentContentElement.parentNode !== this.splitViewHostElement) {
      this.persistentContentElement.parentNode.removeChild(this.persistentContentElement);
    }

    if (this.persistentContentElement.parentNode === this.splitViewHostElement) {
      this.contentReady.set(true);
      return true;
    }

    this.splitViewHostElement.innerHTML = '';
    this.splitViewHostElement.appendChild(this.persistentContentElement);
    this.contentReady.set(true);
    return true;
  }

  private openOverlay(): void {
    if (!this.overlayComponentRef) {
      this.overlayComponentRef = createComponent(DynamicSidebarComponent, {
        environmentInjector: this.injector
      });
      this.appRef.attachView(this.overlayComponentRef.hostView);
      const domElem = (this.overlayComponentRef.hostView as any).rootNodes[0] as HTMLElement;
      document.body.appendChild(domElem);
      this.overlayComponentRef.instance.onClose = () => {
        this.close();
      };
    }

    if (this.persistentContentElement) {
      this.overlayComponentRef.instance.attachContent(this.persistentContentElement);
    }

    this.overlayComponentRef.instance.open();
    this.contentReady.set(true);
  }

  private closeOverlay(): void {
    if (this.overlayComponentRef) {
      this.overlayComponentRef.instance.visible = false;
      setTimeout(() => {
        if (this.overlayComponentRef) {
          this.overlayComponentRef.instance.detachContent();
          this.appRef.detachView(this.overlayComponentRef.hostView);
          this.overlayComponentRef.destroy();
          this.overlayComponentRef = null;
        }
      }, SIDEBAR_TIMINGS.OVERLAY_ANIMATION_MS);
    }
  }
}
